import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import EbookDownloadManager from '../../components/EbookDownloadManager';
import { Colors } from '../../utils/colors';
import { Sizes } from '../../utils/sizes';
import { selectIsAuthenticated, selectCurrentUser } from '../../redux/slices/authSlice';

const Page = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #080808;
  padding-bottom: 60px;
`;

/* ─── Hero ───────────────────────────────────────────────────────────────── */
const Hero = styled.div`
  width: 100%;
  background: linear-gradient(180deg, rgba(255,215,0,0.07) 0%, transparent 100%);
  border-bottom: 1px solid rgba(255,215,0,0.1);
  padding: 52px 24px 40px;
  text-align: center;

  @media (max-width: ${Sizes.mediaQueryBreak1}) {
    padding: 36px 20px 28px;
  }
`;

const HeroTitle = styled.h1`
  color: #fff;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin: 0 0 8px;

  & span { color: ${Colors.yellow}; }

  @media (max-width: ${Sizes.mediaQueryBreak1}) {
    font-size: 26px;
  }
`;

const HeroSub = styled.p`
  color: rgba(255,255,255,0.35);
  font-size: 14px;
  margin: 0;
`;

/* ─── Conteúdo ───────────────────────────────────────────────────────────── */
const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 24px 0;

  @media (max-width: ${Sizes.mediaQueryBreak1}) {
    padding: 28px 16px 0;
  }
`;

/* ─── CTA de login ───────────────────────────────────────────────────────── */
const LoginCard = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 36px 32px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.09);
  border-radius: 16px;
  text-align: center;

  & h2 {
    color: #fff;
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 10px;
  }

  & p {
    color: rgba(255,255,255,0.4);
    font-size: 14px;
    line-height: 1.6;
    margin: 0 0 24px;
  }
`;

const LoginActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryLink = styled(Link)`
  display: inline-block;
  padding: 10px 24px;
  background: ${Colors.yellow};
  color: #111;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 800;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: background 0.18s, transform 0.18s;

  &:hover { background: #ffe54e; transform: translateY(-1px); }
`;

const SecondaryLink = styled(Link)`
  display: inline-block;
  padding: 10px 24px;
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.6);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: border-color 0.18s, color 0.18s;

  &:hover { border-color: rgba(255,215,0,0.3); color: ${Colors.yellow}; }
`;

/* ─── Banner "salve em conta" (usuário anônimo com compras) ──────────────── */
const SaveBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: rgba(255,215,0,0.05);
  border: 1px solid rgba(255,215,0,0.15);
  border-radius: 12px;
  padding: 14px 20px;
  margin-bottom: 28px;
  flex-wrap: wrap;

  & p {
    color: rgba(255,255,255,0.55);
    font-size: 13px;
    margin: 0;
    flex: 1;
    min-width: 200px;
    line-height: 1.5;
    & strong { color: ${Colors.yellow}; }
  }
`;

const BannerActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

/* ─── Footer da página ───────────────────────────────────────────────────── */
const PageFooter = styled.div`
  text-align: center;
  margin-top: 48px;
`;

const BackBtn = styled.button`
  background: none;
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.4);
  padding: 10px 22px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.3px;
  transition: border-color 0.18s, color 0.18s;

  &:hover { border-color: rgba(255,215,0,0.3); color: ${Colors.yellow}; }
`;

/* ═══════════════════════════════════════════════════════════════════════════ */

export default function MyDownloadsPage() {
  const navigate        = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const currentUser     = useSelector(selectCurrentUser);
  const purchasedEbooks = useSelector((state) => state.ebookAccess?.purchasedEbooks || []);
  const hasLocalPurchases = purchasedEbooks.length > 0;

  const firstName = currentUser?.name?.split(' ')[0];

  return (
    <Page>
      <Hero>
        <HeroTitle>
          {firstName ? `Olá, ${firstName} — ` : ''}
          Minhas <span>Compras</span>
        </HeroTitle>
        <HeroSub>Acesse e baixe seus eBooks comprados a qualquer momento.</HeroSub>
      </Hero>

      <Content>
        {/* Sem login e sem compras locais */}
        {!isAuthenticated && !hasLocalPurchases && (
          <LoginCard>
            <h2>Acesse seus eBooks</h2>
            <p>
              Entre na sua conta para ver os eBooks que você comprou —
              inclusive de outros dispositivos.
            </p>
            <LoginActions>
              <PrimaryLink to="/login">Entrar</PrimaryLink>
              <SecondaryLink to="/cadastro">Criar conta</SecondaryLink>
            </LoginActions>
          </LoginCard>
        )}

        {/* Tem compras locais mas não está logado */}
        {!isAuthenticated && hasLocalPurchases && (
          <SaveBanner>
            <p>
              <strong>Salve suas compras na conta</strong> para acessá-las
              em qualquer dispositivo, mesmo se limpar o navegador.
            </p>
            <BannerActions>
              <PrimaryLink to="/cadastro">Criar conta</PrimaryLink>
              <SecondaryLink to="/login">Entrar</SecondaryLink>
            </BannerActions>
          </SaveBanner>
        )}

        {/* Conteúdo de downloads */}
        {(isAuthenticated || hasLocalPurchases) && (
          <>
            <EbookDownloadManager />
            <PageFooter>
              <BackBtn onClick={() => navigate('/loja')}>← Voltar à loja</BackBtn>
            </PageFooter>
          </>
        )}
      </Content>
    </Page>
  );
}
